import { useState, useEffect, useRef } from 'react';
import { X, CreditCard, Check } from 'lucide-react';
import * as api from '../lib/api';
import type { User } from '../lib/types';
import './PaymentModal.css';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'https://backend.heads-up-poker.ru';

interface Order {
  id: number;
  items: any[];
  total_price: number;
  tournament_name?: string;
}

interface Props {
  user: User;
  orders: Order[];
  total: number;
  onClose: () => void;
  onSuccess: () => void;
}

type Step = 'receipt' | 'paying' | 'success' | 'error';

export default function PaymentModal({ user, orders, total, onClose, onSuccess }: Props) {
  const [step, setStep] = useState<Step>('receipt');
  const [error, setError] = useState('');
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [menuMap, setMenuMap] = useState<Record<number, string>>({});
  const widgetRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load menu item names
  useEffect(() => {
    api.getMenuItems().then(items => {
      const map: Record<number, string> = {};
      (items || []).forEach((i: any) => { map[i.id] = i.name; });
      setMenuMap(map);
    }).catch(() => {});
  }, []);

  const handlePay = async () => {
    setStep('paying');
    setError('');

    try {
      // Create payment on backend — pass orders so backend doesn't need to query DB
      const res = await fetch(`${BACKEND_URL}/api/payments/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          user_email: user.email || undefined,
          orders: orders.map(o => ({ id: o.id, total_price: o.total_price, items: o.items })),
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.confirmation_token) {
        throw new Error(data.error || 'Не удалось создать платёж');
      }

      setPaymentId(data.payment_id);

      // Save to localStorage for recovery if page redirects or closes
      localStorage.setItem('pending_payment', JSON.stringify({
        payment_id: data.payment_id,
        order_ids: orders.map(o => o.id),
      }));

      // Load YooKassa widget script on demand (not in index.html to avoid blocking page load)
      if (!(window as any).YooMoneyCheckoutWidget) {
        await new Promise<void>((resolve, reject) => {
          const s = document.createElement('script');
          s.src = 'https://yookassa.ru/checkout-widget/v1/checkout-widget.js';
          s.onload = () => resolve();
          s.onerror = () => reject(new Error('Не удалось загрузить виджет оплаты'));
          document.head.appendChild(s);
        });
      }

      // Initialize YooKassa widget
      const YooMoneyCheckoutWidget = (window as any).YooMoneyCheckoutWidget;
      if (!YooMoneyCheckoutWidget) {
        throw new Error('Виджет оплаты не загружен');
      }

      const checkout = new YooMoneyCheckoutWidget({
        confirmation_token: data.confirmation_token,
        return_url: `${window.location.origin}/orders`,
        error_callback: (err: any) => {
          console.error('YooKassa widget error:', err);
          setError('Ошибка оплаты. Попробуйте ещё раз.');
          setStep('receipt');
        },
      });

      // Render widget
      checkout.render('yookassa-widget');

      // Start polling for payment status
      pollRef.current = setInterval(async () => {
        try {
          const statusRes = await fetch(`${BACKEND_URL}/api/payments/status/${data.payment_id}`);
          const statusData = await statusRes.json();
          if (statusData.status === 'succeeded') {
            if (pollRef.current) clearInterval(pollRef.current);
            checkout.destroy();
            localStorage.removeItem('pending_payment');
            setStep('success');
            // Mark orders paid in Supabase — best effort, don't block success UI
            Promise.all(orders.map(o => api.markOrderPaid(o.id, true)))
              .catch(e => console.error('markOrderPaid failed:', e));
            setTimeout(() => onSuccess(), 2000);
          } else if (statusData.status === 'canceled') {
            if (pollRef.current) clearInterval(pollRef.current);
            checkout.destroy();
            setError('Платёж отменён');
            setStep('receipt');
          }
        } catch {}
      }, 3000);
    } catch (e: any) {
      setError(e.message || 'Ошибка');
      setStep('receipt');
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  return (
    <div className="pay-overlay">
      <div className="pay-modal">
        <button className="pay-close" onClick={onClose}><X size={20} /></button>

        {step === 'receipt' && (
          <>
            <div className="pay-icon"><CreditCard size={28} /></div>
            <h2 className="pay-title">Оплата</h2>

            <div className="pay-items">
              {orders.map(order => (
                <div key={order.id} className="pay-item">
                  <div className="pay-item-name">
                    {order.items.map((i: any, idx: number) => (
                      <span key={idx}>
                        {i.name || menuMap[i.menu_item_id] || `Товар #${i.menu_item_id}`}
                        {i.quantity > 1 ? ` ×${i.quantity}` : ''}
                        {idx < order.items.length - 1 ? ', ' : ''}
                      </span>
                    ))}
                  </div>
                  <div className="pay-item-price">{order.total_price} ₽</div>
                </div>
              ))}
            </div>

            <div className="pay-total">
              <span>Итого</span>
              <span className="pay-total-value">{total} ₽</span>
            </div>

            {error && <div className="pay-error">{error}</div>}

            <button className="pay-btn" onClick={handlePay}>
              Оплатить {total} ₽
            </button>
          </>
        )}

        {step === 'paying' && (
          <>
            <h2 className="pay-title">Оплата</h2>
            <div id="yookassa-widget" ref={widgetRef} className="pay-widget" />
          </>
        )}

        {step === 'success' && (
          <div className="pay-success">
            <div className="pay-success-icon"><Check size={32} /></div>
            <h2>Оплата прошла!</h2>
            <p>Спасибо за оплату</p>
          </div>
        )}
      </div>
    </div>
  );
}
